import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { fetchMe, exchangeSession, logout as apiLogout, fetchInboxCount, claimReferral, updateProfile } from "../lib/api";
import { writePrefs } from "../lib/prefs";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inboxCount, setInboxCount] = useState(0);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const u = await fetchMe();
      setUser(u);
      // Stash the first name for the landing-page "WELCOME BACK" CTA.
      if (u?.name) {
        const first = String(u.name).split(" ")[0];
        writePrefs({ last_name: first });
      }
    } catch {
      setUser(null);
    }
  }, []);

  const refreshInboxCount = useCallback(async () => {
    try {
      const r = await fetchInboxCount();
      setInboxCount(r?.pending || 0);
    } catch {
      setInboxCount(0);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  // Poll inbox every 30s while signed in. Cheap (counts only).
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (!user) { setInboxCount(0); return; }
    refreshInboxCount();
    pollRef.current = setInterval(refreshInboxCount, 30000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [user, refreshInboxCount]);

  // Once signed in, consume any pending referral code from localStorage.
  // Server is idempotent — repeat calls are safe no-ops once `referred_by`
  // is set, so a simple "fire once per session" pattern is sufficient.
  const refClaimedRef = useRef(false);
  useEffect(() => {
    if (!user || refClaimedRef.current) return;
    let code = null;
    try { code = localStorage.getItem("lhub_ref"); } catch { /* ignore */ }
    if (!code) return;
    refClaimedRef.current = true;
    claimReferral(code)
      .then(() => { try { localStorage.removeItem("lhub_ref"); } catch {} })
      .catch(() => { /* invalid code — drop silently */ });
  }, [user]);

  // Auto-detect the user's IANA timezone once per session and sync to the
  // server so /api/team-challenges scheduling shows the host's TZ to viewers.
  const tzSyncedRef = useRef(false);
  useEffect(() => {
    if (!user || tzSyncedRef.current) return;
    let detected = null;
    try { detected = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* ignore */ }
    if (!detected || detected === user.tz) { tzSyncedRef.current = true; return; }
    tzSyncedRef.current = true;
    updateProfile({ tz: detected }).then((u) => setUser(u)).catch(() => { /* best-effort */ });
  }, [user]);

  const loginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processEmergentSession = async (session_id) => {
    const data = await exchangeSession(session_id);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    await apiLogout();
    setUser(null);
  };

  return (
    <AuthCtx.Provider
      value={{
        user, setUser, loading, loginGoogle, processEmergentSession, logout, refresh,
        inboxCount, refreshInboxCount,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
